import { Toggle } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import { CreateFullPageStep } from '@carbon/ibm-products';
import { useMemo } from 'react';

import styles from './GeneralDetails.module.scss';

import { EMAIL_PATTERN } from 'utils/constants';
import sharedMessages from 'Components/utils/sharedMessages';
import { useEntityContext } from 'Screens/Dashboard/AllEntities/EntityProvider/EntityContext';
import EntityNameInput from 'Screens/Dashboard/AllEntities/Details/Inputs/NameInput';
import EntityPhoneNumberInput from 'Screens/Dashboard/AllEntities/Details/Inputs/PhoneNumberInput';
import EntityEmailInput from 'Screens/Dashboard/AllEntities/Details/Inputs/EntityEmailInput/EntityEmailInput';
import EntityWebsiteInput from 'Screens/Dashboard/AllEntities/Details/Inputs/WebsiteInput';

export type EntityDetailsMode = 'parish' | 'parishGroup' | 'independent' | 'edit';

type Props = {
    withStep?: boolean;
    mode?: EntityDetailsMode;
    showUsesPoints?: boolean;
}

const GeneralDetails = ({
    withStep = true,
    mode = 'edit',
    showUsesPoints = false,
}: Props) => {
    const intl = useIntl();
    const { state, dispatch } = useEntityContext();
    const { name, email, usesPoints } = state;

    const isEmailValid = useMemo(() => EMAIL_PATTERN.test(email) || email.length === 0, [email]);

    const showContactFields = mode === 'parish' || mode === 'edit';

    const isDisabledSubmit = (name.length === 0
        || (showContactFields && !isEmailValid && email.length > 0));

    const onUsesPointsToggle = (checked: boolean) => {
        dispatch({ type: 'SET_USES_POINTS', payload: checked });
    };

    const content = (
        <div className={styles.group}>
            <EntityNameInput />
            {showContactFields && (
                <>
                    <EntityEmailInput />
                    <EntityPhoneNumberInput />
                    <EntityWebsiteInput />
                </>
            )}
            {showUsesPoints && (
                <div className={styles.toggleRow}>
                    <span className={styles.toggleLabel}>
                        {intl.formatMessage(messages.activePoints)}
                    </span>
                    <Toggle
                        id="uses-points-toggle"
                        labelA={intl.formatMessage(messages.no)}
                        labelB={intl.formatMessage(messages.yes)}
                        toggled={usesPoints}
                        onToggle={onUsesPointsToggle}
                    />
                </div>
            )}
        </div>
    );

    if (!withStep) {
        return content;
    }

    return (
        <CreateFullPageStep
            disableSubmit={isDisabledSubmit}
            fieldsetLegendText=""
            title={intl.formatMessage(sharedMessages.basic)}
        >
            {content}
        </CreateFullPageStep>
    );
};

const messages = defineMessages({
    activePoints: {
        id: 'GeneralDetails.activePoints',
        defaultMessage: 'Punkty aktywne',
    },
    usesPointsLabel: {
        id: 'GeneralDetails.usesPointsLabel',
        defaultMessage: 'Czy grupa będzie używać punktacji? {value}',
    },
    yes: {
        id: 'GeneralDetails.yes',
        defaultMessage: 'Tak',
    },
    no: {
        id: 'GeneralDetails.no',
        defaultMessage: 'Nie',
    },
});

export default GeneralDetails;
